import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  ShoppingCart as ShoppingCartIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../services/api';
import { toast } from 'react-toastify';

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('sales');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
    end: new Date()
  });
  const [reportData, setReportData] = useState({
    sales: [],
    users: [],
    products: [],
    summary: {
      totalSales: 0,
      totalOrders: 0,
      totalUsers: 0,
      totalProducts: 0,
      growth: 0
    }
  });

  useEffect(() => {
    fetchReportData();
  }, [reportType, dateRange]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      // Simulate API calls based on report type
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock data - replace with actual API calls
      const mockData = {
        sales: [
          { id: 1, date: '2024-01-01', amount: 1250, orders: 15, growth: 12 },
          { id: 2, date: '2024-01-02', amount: 980, orders: 12, growth: -5 },
          { id: 3, date: '2024-01-03', amount: 1450, orders: 18, growth: 8 },
          { id: 4, date: '2024-01-04', amount: 1100, orders: 14, growth: 15 },
          { id: 5, date: '2024-01-05', amount: 1350, orders: 16, growth: 3 }
        ],
        users: [
          { id: 1, name: 'John Doe', email: 'john@example.com', signupDate: '2024-01-01', orders: 5, totalSpent: 450 },
          { id: 2, name: 'Jane Smith', email: 'jane@example.com', signupDate: '2024-01-02', orders: 3, totalSpent: 280 },
          { id: 3, name: 'Bob Johnson', email: 'bob@example.com', signupDate: '2024-01-03', orders: 7, totalSpent: 620 }
        ],
        products: [
          { id: 1, name: 'Lipstick Set', category: 'Makeup', sales: 45, revenue: 1350, stock: 120 },
          { id: 2, name: 'Foundation', category: 'Makeup', sales: 32, revenue: 960, stock: 85 },
          { id: 3, name: 'Mascara', category: 'Eye Care', sales: 28, revenue: 560, stock: 95 }
        ],
        summary: {
          totalSales: 12500,
          totalOrders: 156,
          totalUsers: 89,
          totalProducts: 45,
          growth: 8.5
        }
      };
      
      setReportData(mockData);
    } catch (error) {
      toast.error('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    toast.success('Report exported successfully!');
  };

  const getReportColumns = () => {
    switch (reportType) {
      case 'sales':
        return [
          { key: 'date', label: 'Date' },
          { key: 'amount', label: 'Sales Amount' },
          { key: 'orders', label: 'Orders' },
          { key: 'growth', label: 'Growth %' }
        ];
      case 'users':
        return [
          { key: 'name', label: 'Name' },
          { key: 'email', label: 'Email' },
          { key: 'signupDate', label: 'Signup Date' },
          { key: 'orders', label: 'Orders' },
          { key: 'totalSpent', label: 'Total Spent' }
        ];
      case 'products':
        return [
          { key: 'name', label: 'Product Name' },
          { key: 'category', label: 'Category' },
          { key: 'sales', label: 'Sales' },
          { key: 'revenue', label: 'Revenue' },
          { key: 'stock', label: 'Stock' }
        ];
      default:
        return [];
    }
  };

  const getReportData = () => {
    switch (reportType) {
      case 'sales':
        return reportData.sales;
      case 'users':
        return reportData.users;
      case 'products':
        return reportData.products;
      default:
        return [];
    }
  };

  const SummaryCard = ({ title, value, icon, trend, trendValue, color = 'primary' }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="text.secondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold">
              {value}
            </Typography>
            {trend && (
              <Box display="flex" alignItems="center" mt={1}>
                {trend === 'up' ? (
                  <TrendingUpIcon color="success" fontSize="small" />
                ) : (
                  <TrendingDownIcon color="error" fontSize="small" />
                )}
                <Typography 
                  variant="body2" 
                  color={trend === 'up' ? 'success.main' : 'error.main'}
                  sx={{ ml: 0.5 }}
                >
                  {trendValue}
                </Typography>
              </Box>
            )}
          </Box>
          <Box color={`${color}.main`}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <LoadingSpinner message="Generating report..." />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
          Reports & Analytics
        </Typography>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Total Sales"
              value={`$${reportData.summary.totalSales.toLocaleString()}`}
              icon={<MoneyIcon sx={{ fontSize: 40 }} />}
              color="success"
              trend="up"
              trendValue="+8.5%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Total Orders"
              value={reportData.summary.totalOrders}
              icon={<ShoppingCartIcon sx={{ fontSize: 40 }} />}
              color="primary"
              trend="up"
              trendValue="+12%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Total Users"
              value={reportData.summary.totalUsers}
              icon={<PeopleIcon sx={{ fontSize: 40 }} />}
              color="info"
              trend="up"
              trendValue="+5%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Total Products"
              value={reportData.summary.totalProducts}
              icon={<AssessmentIcon sx={{ fontSize: 40 }} />}
              color="warning"
              trend="up"
              trendValue="+3"
            />
          </Grid>
        </Grid>

        {/* Report Controls */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Report Type</InputLabel>
                  <Select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    label="Report Type"
                  >
                    <MenuItem value="sales">Sales Report</MenuItem>
                    <MenuItem value="users">User Report</MenuItem>
                    <MenuItem value="products">Product Report</MenuItem>
                    <MenuItem value="inventory">Inventory Report</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Start Date"
                  value={dateRange.start}
                  onChange={(date) => setDateRange(prev => ({ ...prev, start: date }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="End Date"
                  value={dateRange.end}
                  onChange={(date) => setDateRange(prev => ({ ...prev, end: date }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={fetchReportData}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExport}
                  >
                    Export
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Report Table */}
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                {reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getReportData().length} records found
              </Typography>
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    {getReportColumns().map((column) => (
                      <TableCell key={column.key}>
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getReportData().map((row, index) => (
                    <TableRow key={row.id || index}>
                      {getReportColumns().map((column) => (
                        <TableCell key={column.key}>
                          {column.key === 'growth' ? (
                            <Chip
                              label={`${row[column.key]}%`}
                              color={row[column.key] >= 0 ? 'success' : 'error'}
                              size="small"
                            />
                          ) : column.key === 'amount' || column.key === 'revenue' || column.key === 'totalSpent' ? (
                            `$${row[column.key].toLocaleString()}`
                          ) : (
                            row[column.key]
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default Reports;
